import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Label, Input } from 'reactstrap';
import _ from 'underscore';
import Icon from '../containers/icon';

/**
 *  Provide search to table views.
 *
 *  This needs to be a controlled component that keeps its
 *  state updated with the latest props, which is necessary
 *  so that search input can be buffered (i.e., debounced).
 *  Found a great React debouncing thread at
 *  http://stackoverflow.com/questions/23123138/perform-debounce-in-react-js.
 */
const SearchInput = ({
  className: _className = '',
  label: _label = null,
  loading: _loading = false,
  onChange,
  onClear,
  size: _size = 'lg',
  text: _text = null,
  placeholder: _placeholder = 'Search'
}) => {
  const [query, setQuery] = useState(_text || '');

  // Use a ref to always have access to the latest onChange callback.
  // This allows the debounce function to remain stable while still calling
  // the most up-to-date callback when it fires.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create the debounce function only once (on mount).
  // It calls through onChangeRef to always use the latest onChange callback.
  // This prevents the debounce from being cancelled/recreated when the parent
  // component re-renders and passes a new onChange function reference.
  const delayedQuery = useMemo(() => _.debounce(q => onChangeRef.current(q), 500), [] // Empty deps - created only once
  );

  // Cleanup: cancel debounce on unmount
  useEffect(() => () => {
    delayedQuery.cancel();
  }, [delayedQuery]);

  // refs are state variables that don't cause rerender. This is useful to compare the
  // props causing rerender against the last search query.
  const lastQuery = useRef();
  useEffect(() => {
    // when text prop is set to '' or null, clear the search.
    if (lastQuery.current && (_text === '' || _text === null)) {
      setQuery('');
      onChangeRef.current('');
    } else if (_text !== null && _text !== void 0 && _text.length && _text !== query && query === lastQuery.current) {
      // if there's incoming text and it doesn't match the query, but the query
      // matches the reference lastQuery a cabinet switch has occurred and the UI should
      // reset the search value
      setQuery(_text);
    }
    lastQuery.current = _text;
  }, [query, _text]);
  const handleSearch = e => {
    setQuery(e.target.value);
    delayedQuery(e.target.value);
  };
  const renderActionIcon = () => {
    if (_loading) {
      return /*#__PURE__*/React.createElement("span", {
        className: "input-spinner t-si-loading",
        "data-testid": "t-si-loading"
      }, /*#__PURE__*/React.createElement(Icon, {
        glyph: "spinner",
        className: "fa-spin"
      }));
    }
    if (_text) {
      return /*#__PURE__*/React.createElement("span", {
        "aria-label": "clear-search-field",
        "data-testid": "t-si-clear",
        role: "button",
        tabIndex: 0,
        className: "clearMe t-si-clear",
        onKeyDown: onClear,
        onClick: onClear
      }, /*#__PURE__*/React.createElement(Icon, {
        glyph: "times-circle"
      }));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement("form", {
    className: "search"
  }, /*#__PURE__*/React.createElement(FormGroup, null, _label ? /*#__PURE__*/React.createElement(Label, null, _label) : null, /*#__PURE__*/React.createElement("div", {
    className: "input-group mb-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group-prepend"
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group-text"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "search"
  }))), /*#__PURE__*/React.createElement(Input, {
    "data-testid": "t-si-input",
    id: "search",
    type: "text",
    value: query,
    placeholder: _placeholder,
    bsSize: _size,
    onChange: handleSearch,
    className: `t-si-input ${_className} tqc-control`,
    onKeyDown: event => {
      if (event.keyCode === 13) {
        event.stopPropagation();
        event.preventDefault();
        onChangeRef.current(event.target.value);
      }
    }
  }), renderActionIcon())));
};
SearchInput.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  size: PropTypes.string,
  label: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  text: PropTypes.string
};
export default SearchInput;